import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Paper,
  Button,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
} from '@mui/material';
import { LocalHospital, ArrowBack, Add, Edit, Delete, Search } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { serviceRateService } from '../../services/api';

const emptyForm = { serviceName: '', rate: '' };

const ServiceManagement = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await serviceRateService.getAll();
      setServices(res.data);
      setFiltered(res.data);
      setSearchQuery('');
    } catch {
      setError('Failed to load services.');
    }
  };

  const handleSearch = () => {
    const q = searchQuery.trim().toLowerCase();
    setFiltered(
      q ? services.filter((s) => s.serviceName.toLowerCase().includes(q)) : services
    );
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({ serviceName: item.serviceName, rate: String(item.rate) });
    } else {
      setEditingItem(null);
      setFormData(emptyForm);
    }
    setError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData(emptyForm);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const rate = parseFloat(formData.rate);
    if (!formData.serviceName.trim()) { setError('Service name is required.'); return; }
    if (isNaN(rate) || rate < 0)      { setError('Rate must be a valid non-negative number.'); return; }
    try {
      const payload = { serviceName: formData.serviceName.trim(), rate };
      if (editingItem) {
        await serviceRateService.update(editingItem.id, { id: editingItem.id, ...payload });
      } else {
        await serviceRateService.create(payload);
      }
      handleCloseDialog();
      loadData();
    } catch {
      setError('Failed to save. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      await serviceRateService.delete(id);
      loadData();
    } catch {
      setError('Failed to delete service.');
    }
  };

  const columns = [
    { field: 'id', headerName: 'Service ID', width: 110 },
    { field: 'serviceName', headerName: 'Service Name', flex: 1, minWidth: 220 },
    {
      field: 'rate',
      headerName: 'Rate (₹)',
      width: 130,
      renderCell: (params) => `₹ ${Number(params.value).toFixed(2)}`,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" color="primary" title="Edit" onClick={() => handleOpenDialog(params.row)}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" title="Delete" onClick={() => handleDelete(params.row.id)}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <AppBar position="static" className="medical-gradient">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <LocalHospital sx={{ fontSize: 40, mr: 2 }} />
          <Typography variant="h5" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Manage Services &amp; Rates
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {error && !openDialog && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
        )}

        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Search service..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                sx={{ width: 260 }}
              />
              <Button variant="outlined" startIcon={<Search />} onClick={handleSearch}>
                Search
              </Button>
            </Box>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
              Add Service
            </Button>
          </Box>

          <DataGrid
            rows={filtered}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            autoHeight
            disableSelectionOnClick
          />
        </Paper>
      </Container>

      {/* Add / Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Service' : 'Add Service'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Grid container spacing={2}>
              {editingItem && (
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth label="Service ID" value={editingItem.id}
                    disabled InputProps={{ readOnly: true }}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={editingItem ? 6 : 8}>
                <TextField
                  fullWidth label="Service Name" required autoFocus
                  placeholder="e.g., Consultation"
                  value={formData.serviceName}
                  onChange={(e) => setFormData((p) => ({ ...p, serviceName: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={editingItem ? 3 : 4}>
                <TextField
                  fullWidth label="Rate (₹)" required type="number"
                  inputProps={{ min: 0, step: '0.01' }}
                  placeholder="0.00"
                  value={formData.rate}
                  onChange={(e) => setFormData((p) => ({ ...p, rate: e.target.value }))}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingItem ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default ServiceManagement;
